import { useCallback } from "react";
import type { ProductQuery } from "../../../__generated__/graphql";

type Product = ProductQuery["product"];

/** Cart에 넣을 때 사용하는 상품/스토어 shape (core/cart와 호환) */
interface CartProductShape {
  id: number;
  name: string;
  price: number;
  discountPrice: number;
  userPrice: number;
  quantity: number;
  currentCount: number;
  description: string;
  saleEndTime: string;
  createdAt: string;
  updatedAt: string;
  store: { id: number };
  img: string;
  menus: { id: number; img: string }[] | null;
}

interface CartStoreShape {
  id: number;
  closingHours: string;
  address: string;
  title: string;
}

type AddToCartFn = (
  product: CartProductShape,
  quantity: number,
  store: CartStoreShape
) => void;

export function useProductCart(
  product: Product | null | undefined,
  quantity: number,
  addToCart: AddToCartFn,
  cart: Array<{ product: { id: number }; quantity: number }>,
  navigate: (path: string) => void
) {
  const cartItem = cart.find((item) => item.product.id === product?.id);
  const cartQuantityInCart = cartItem?.quantity ?? 0;

  const addProductToCart = useCallback(() => {
    if (!product) return;
    const cartProduct: CartProductShape = {
      ...product,
      description: product.description ?? "",
      saleEndTime: product.saleEndTime ?? "",
      createdAt: String(product.createdAt),
      updatedAt: String(product.updatedAt),
      store: { id: product.store.id },
      img: product.img ?? "",
      menus: product.menus ? product.menus.map((m) => ({ id: m.id, img: m.img })) : null,
    };
    addToCart(
      cartProduct,
      quantity,
      {
        id: product.store.id,
        closingHours: product.store.closingHours ?? "",
        address: product.store.address ?? "",
        title: product.store.title,
      }
    );
    navigate(`/store/${product.store.id}`);
  }, [product, quantity, addToCart, navigate]);

  return { cartQuantityInCart, addProductToCart };
}
